import axios, { AxiosInstance } from 'axios';
import {
    Building,
    School,
    Class,
    ResponsibilityCenter,
    LocalityResponse,
    BUILDING_SCIM_ATTRIBUTES,
    SCHOOL_SCIM_ATTRIBUTES,
    CLASS_SCIM_ATTRIBUTES,
    RESPONSIBILITY_CENTER_SCIM_ATTRIBUTES,
    mapScimToBuilding,
    mapScimToSchool,
    mapScimToClass,
    mapScimToResponsibilityCenter,
    FullSchool
} from '../types/Localities';
import { getScimConfig } from '../utils/envUtils';
import { ScimAuthService } from './ScimAuthService';
import { debugService } from './DebugService';
import { getAxiosInstance } from './axiosInstance';

/**
 * Service for efficient Building-based API calls using SCIM attribute selection
 * Reduces server load by only requesting the fields needed for table views
 */
export class ScimBuildingService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
    }

    async searchBuildings(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        searchtext?: string;
        omitSearchDialectExpression?: boolean;
        searchAttributes?: string;
        abortSignal?: AbortSignal;
    }): Promise<LocalityResponse<Building>> {
        try {
            const queryParams: any = {
                attributes: BUILDING_SCIM_ATTRIBUTES.join(','),
                skipReferenceExpansion: true
            };

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.searchtext) {
                queryParams.searchtext = params.searchtext;
                queryParams.searchdialect = params.omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            if (params.searchAttributes) {
                queryParams.searchAttributes = params.searchAttributes;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            if (params.sortBy) {
                const sortOrder = params.sortOrder || 'asc';
                queryParams.sortBy = params.sortBy;
                queryParams.sortOrder = sortOrder;
            }

            if (debugService.isEnabled()) {
                queryParams.Debug = 'true';
            }

            const response = await this.api.get('/entities/building', {
                params: queryParams,
                signal: params.abortSignal
            });

            const data = response.data;
            const buildings: Building[] = (data.Resources || []).map(mapScimToBuilding);

            const totalResults = data.totalResults || 0;
            const itemsPerPage = params.pageSize || 25;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            const listLevelDollarProps = data && typeof data === 'object'
                ? Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith('$')))
                : {};

            return {
                ...listLevelDollarProps,
                localities: buildings,
                items: buildings,
                data: buildings,
                totalResults,
                currentPage: params.page || 1,
                totalPages,
                hasNextPage: (params.page || 1) < totalPages,
                hasPreviousPage: (params.page || 1) > 1,
                itemsPerPage,
                filter: params.filter
            };

        } catch (error) {
            if (!axios.isCancel(error)) console.error('❌ ScimBuildingService: Search failed:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    async getBuildingById(id: string): Promise<Building | null> {
        try {
            const queryParams = {
                attributes: BUILDING_SCIM_ATTRIBUTES.join(',')
            };

            const response = await this.api.get(`/entities/building/${id}`, {
                params: queryParams
            });

            const data = response.data;
            return mapScimToBuilding(data);

        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error(`❌ ScimBuildingService: Failed to get building ${id}:`, error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    // Singleton instance
    private static instance: ScimBuildingService;
    public static get scimBuildingService(): ScimBuildingService {
        if (!ScimBuildingService.instance) {
            ScimBuildingService.instance = new ScimBuildingService();
        }
        return ScimBuildingService.instance;
    }
}

/**
 * Service for efficient School-based API calls using SCIM attribute selection
 */
export class ScimSchoolService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
    }

    async searchSchools(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        searchtext?: string;
        omitSearchDialectExpression?: boolean;
        searchAttributes?: string;
        abortSignal?: AbortSignal;
    }): Promise<LocalityResponse<School>> {
        try {
            const queryParams: any = {
                attributes: SCHOOL_SCIM_ATTRIBUTES.join(','),
                skipReferenceExpansion: true
            };

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.searchtext) {
                queryParams.searchtext = params.searchtext;
                queryParams.searchdialect = params.omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            if (params.searchAttributes) {
                queryParams.searchAttributes = params.searchAttributes;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            if (params.sortBy) {
                const sortOrder = params.sortOrder || 'asc';
                queryParams.sortBy = params.sortBy;
                queryParams.sortOrder = sortOrder;
            }

            if (debugService.isEnabled()) {
                queryParams.Debug = 'true';
            }

            const response = await this.api.get('/entities/school', {
                params: queryParams,
                signal: params.abortSignal
            });

            const data = response.data;
            const schools: School[] = (data.Resources || []).map(mapScimToSchool);

            const totalResults = data.totalResults || 0;
            const itemsPerPage = params.pageSize || 25;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            const listLevelDollarProps = data && typeof data === 'object'
                ? Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith('$')))
                : {};

            return {
                ...listLevelDollarProps,
                localities: schools,
                items: schools,
                data: schools,
                totalResults,
                currentPage: params.page || 1,
                totalPages,
                hasNextPage: (params.page || 1) < totalPages,
                hasPreviousPage: (params.page || 1) > 1,
                itemsPerPage,
                filter: params.filter
            };

        } catch (error) {
            if (!axios.isCancel(error)) console.error('❌ ScimSchoolService: Search failed:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    async getSchoolById(id: string): Promise<School | null> {
        try {
            const queryParams = {
                attributes: SCHOOL_SCIM_ATTRIBUTES.join(',')
            };

            const response = await this.api.get(`/entities/school/${id}`, {
                params: queryParams
            });

            const data = response.data;
            return mapScimToSchool(data);

        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error(`❌ ScimSchoolService: Failed to get school ${id}:`, error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    async getFullSchoolsForMap(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
    }): Promise<LocalityResponse<FullSchool>> {
        try {
            // For maps, we need all the address fields, so we don't specify attributes
            // This will return the full school data
            const queryParams: any = {};

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            const response = await this.api.get('/entities/school', {
                params: queryParams
            });

            const data = response.data;
            const schools: FullSchool[] = (data.Resources || []).map((scimData: any) => ({
                Id: scimData.Id || '',
                DisplayName: scimData.DisplayName || '',
                EntityType: scimData.EntityType || '',
                Address: scimData.Address,
                City: scimData.City,
                State: scimData.State,
                Zip: scimData.Zip,
                PhoneNumber: scimData.PhoneNumber,
                Principals: scimData.Principals,
                Type: scimData.Type,
                Region: scimData.Region,
                RegionLabel: scimData.RegionLabel,
                Role: scimData.Role,
                GradeLevels: scimData.GradeLevels,
                Building: scimData.Building,
                Rc: scimData.Rc,
                ObjectId: scimData.ObjectId
            }));

            const totalResults = data.totalResults || 0;
            const itemsPerPage = params.pageSize || 25;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            return {
                localities: schools,
                totalResults,
                currentPage: params.page || 1,
                totalPages,
                hasNextPage: (params.page || 1) < totalPages,
                hasPreviousPage: (params.page || 1) > 1,
                itemsPerPage,
                filter: params.filter
            };

        } catch (error) {
            console.error('❌ ScimSchoolService: Failed to get full schools for map:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    // Singleton instance
    private static instance: ScimSchoolService;
    public static get scimSchoolService(): ScimSchoolService {
        if (!ScimSchoolService.instance) {
            ScimSchoolService.instance = new ScimSchoolService();
        }
        return ScimSchoolService.instance;
    }
}

/**
 * Service for efficient Class-based API calls using SCIM attribute selection
 */
export class ScimClassService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
    }

    async searchClasses(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        searchtext?: string;
        omitSearchDialectExpression?: boolean;
        searchAttributes?: string;
        abortSignal?: AbortSignal;
    }): Promise<LocalityResponse<Class>> {
        try {
            const queryParams: any = {
                attributes: CLASS_SCIM_ATTRIBUTES.join(','),
                skipReferenceExpansion: true
            };

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.searchtext) {
                queryParams.searchtext = params.searchtext;
                queryParams.searchdialect = params.omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            if (params.searchAttributes) {
                queryParams.searchAttributes = params.searchAttributes;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            if (params.sortBy) {
                const sortOrder = params.sortOrder || 'asc';
                queryParams.sortBy = params.sortBy;
                queryParams.sortOrder = sortOrder;
            }

            if (debugService.isEnabled()) {
                queryParams.Debug = 'true';
            }

            const response = await this.api.get('/entities/class', {
                params: queryParams,
                signal: params.abortSignal
            });

            const data = response.data;
            const classes: Class[] = (data.Resources || []).map(mapScimToClass);

            const totalResults = data.totalResults || 0;
            const itemsPerPage = params.pageSize || 25;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            const listLevelDollarProps = data && typeof data === 'object'
                ? Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith('$')))
                : {};

            return {
                ...listLevelDollarProps,
                localities: classes,
                items: classes,
                data: classes,
                totalResults,
                currentPage: params.page || 1,
                totalPages,
                hasNextPage: (params.page || 1) < totalPages,
                hasPreviousPage: (params.page || 1) > 1,
                itemsPerPage,
                filter: params.filter
            };

        } catch (error) {
            if (!axios.isCancel(error)) console.error('❌ ScimClassService: Search failed:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    async getClassById(id: string): Promise<Class | null> {
        try {
            const queryParams = {
                attributes: CLASS_SCIM_ATTRIBUTES.join(',')
            };

            const response = await this.api.get(`/entities/class/${id}`, {
                params: queryParams
            });

            const data = response.data;
            return mapScimToClass(data);

        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error(`❌ ScimClassService: Failed to get class ${id}:`, error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    // Singleton instance
    private static instance: ScimClassService;
    public static get scimClassService(): ScimClassService {
        if (!ScimClassService.instance) {
            ScimClassService.instance = new ScimClassService();
        }
        return ScimClassService.instance;
    }
}

/**
 * Service for efficient ResponsibilityCenter-based API calls using SCIM attribute selection
 */
export class ScimResponsibilityCenterService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
    }

    async searchResponsibilityCenters(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        searchtext?: string;
        omitSearchDialectExpression?: boolean;
        searchAttributes?: string;
        abortSignal?: AbortSignal;
    }): Promise<LocalityResponse<ResponsibilityCenter>> {
        try {
            const queryParams: any = {
                attributes: RESPONSIBILITY_CENTER_SCIM_ATTRIBUTES.join(','),
                skipReferenceExpansion: true
            };

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.searchtext) {
                queryParams.searchtext = params.searchtext;
                queryParams.searchdialect = params.omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            if (params.searchAttributes) {
                queryParams.searchAttributes = params.searchAttributes;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            if (params.sortBy) {
                const sortOrder = params.sortOrder || 'asc';
                queryParams.sortBy = params.sortBy;
                queryParams.sortOrder = sortOrder;
            }

            if (debugService.isEnabled()) {
                queryParams.Debug = 'true';
            }

            const response = await this.api.get('/entities/responsibilitycenter', {
                params: queryParams,
                signal: params.abortSignal
            });

            const data = response.data;
            const responsibilityCenters: ResponsibilityCenter[] = (data.Resources || []).map(mapScimToResponsibilityCenter);

            const totalResults = data.totalResults || 0;
            const itemsPerPage = params.pageSize || 25;
            const totalPages = Math.ceil(totalResults / itemsPerPage);

            const listLevelDollarProps = data && typeof data === 'object'
                ? Object.fromEntries(Object.entries(data).filter(([key]) => key.startsWith('$')))
                : {};

            return {
                ...listLevelDollarProps,
                localities: responsibilityCenters,
                items: responsibilityCenters,
                data: responsibilityCenters,
                totalResults,
                currentPage: params.page || 1,
                totalPages,
                hasNextPage: (params.page || 1) < totalPages,
                hasPreviousPage: (params.page || 1) > 1,
                itemsPerPage,
                filter: params.filter
            };

        } catch (error) {
            if (!axios.isCancel(error)) console.error('❌ ScimResponsibilityCenterService: Search failed:', error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    async getResponsibilityCenterById(id: string): Promise<ResponsibilityCenter | null> {
        try {
            const queryParams = {
                attributes: RESPONSIBILITY_CENTER_SCIM_ATTRIBUTES.join(',')
            };

            const response = await this.api.get(`/entities/responsibilitycenter/${id}`, {
                params: queryParams
            });

            const data = response.data;
            return mapScimToResponsibilityCenter(data);

        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            console.error(`❌ ScimResponsibilityCenterService: Failed to get responsibility center ${id}:`, error);
            throw ScimAuthService.handleAuthError(error);
        }
    }

    // Singleton instance
    private static instance: ScimResponsibilityCenterService;
    public static get scimResponsibilityCenterService(): ScimResponsibilityCenterService {
        if (!ScimResponsibilityCenterService.instance) {
            ScimResponsibilityCenterService.instance = new ScimResponsibilityCenterService();
        }
        return ScimResponsibilityCenterService.instance;
    }
}

// Export singleton instances
export const scimBuildingService = ScimBuildingService.scimBuildingService;
export const scimSchoolService = ScimSchoolService.scimSchoolService;
export const scimClassService = ScimClassService.scimClassService;
export const scimResponsibilityCenterService = ScimResponsibilityCenterService.scimResponsibilityCenterService;
